import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import { reactRender } from 'reusable/react-render/react-render'
import CssBaseline from "@mui/material/CssBaseline"
import { modalConfig } from './modal-theme'
import GapDialog, { GapDialogProps } from 'reusable/gap-dialog/GapDialog'
import { GapDialogAction } from 'reusable/gap-dialog/GapDialogActionButton'
import ModalState from 'reusable/mobx-helpers/ModalState'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import CheckCircle from '@mui/icons-material/CheckCircle'
import { ErrorOutlined, ReplayOutlined } from '@mui/icons-material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { themeConfig }from '@gap/shared/theme'
import Button from '@mui/material/Button'

const messagePromiseDialog = async (options: GapDialogProps) =>
   new Promise<GapDialogAction>((resolve) => {
      reactRender((container, root) => {
         const modalState = new ModalState({
            initial: true,
            onClose: () => {
               root.unmount()
            }
         })

         return <StyledEngineProvider injectFirst>
            <ThemeProvider theme={modalConfig.theme}>
               <CssBaseline />
               <GapDialog modalState={modalState} {...options} resolve={resolve} size='extra small'>{options.children}</GapDialog>
            </ThemeProvider>
         </StyledEngineProvider>
      })
   })

interface messagePromiseProps {
   contentOnLoad: React.ReactNode
   contentOnFailure: React.ReactNode
   contentOnSuccess: React.ReactNode
   promiseCallback: () => Promise<void>
   withRetry?: boolean
}

/**
 * Shows a dialog with your specified content depending on the state of your given promiseCallback
 */
export async function messagePromise(props: messagePromiseProps): Promise<void> {
   const onLoadModalState = new ModalState({ initial: true })
   const onFailureModalState = new ModalState({ initial: false })
   const onSuccessModalState = new ModalState({ initial: false })

   // While loading
   messagePromiseDialog({
      title: '',
      children: <ContentWrapper state='loading' messagePromiseProps={props} modalState={onLoadModalState} />,
      primary: {
         name: 'Done',
         disabled: true
      },
      modalState: onLoadModalState
   })

   // If failed
   messagePromiseDialog({
      title: '',
      children: <ContentWrapper state='failure' messagePromiseProps={props} modalState={onFailureModalState} />,
      primary: {
         name: 'Done'
      },
      modalState: onFailureModalState
   })

   // If successfull
   messagePromiseDialog({
      title: '',
      children: <ContentWrapper state='loaded' messagePromiseProps={props} modalState={onSuccessModalState} />,
      primary: {
         name: 'Done'
      },
      modalState: onSuccessModalState
   })

   try {
      await props.promiseCallback()
      onSuccessModalState.open()
   } catch {
      onFailureModalState.open()
   } finally {
      onLoadModalState.close()
   }

   return
}

const ContentWrapper = observer(function ContentWrapper(props: { state: 'loading' | 'loaded' | 'failure', messagePromiseProps: messagePromiseProps, modalState: ModalState }) {

   const content = useMemo(() => {
      switch (props.state) {
         case 'loading':
            return {
               Icon: CircularProgress,
               iconColor: themeConfig.primary[800],
               text: props.messagePromiseProps.contentOnLoad
            }
         case 'loaded':
            return {
               Icon: CheckCircle,
               iconColor: themeConfig.system.success[600],
               text: props.messagePromiseProps.contentOnSuccess
            }
         case 'failure':
            return {
               Icon: ErrorOutlined,
               iconColor: themeConfig.system.error[500],
               text: props.messagePromiseProps.contentOnFailure
            }
      }
   }, [props])

   const retryCallback = useCallback(() => {
      props.modalState.close()
      messagePromise(props.messagePromiseProps)
   }, [props.messagePromiseProps, props.modalState])

   return <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      mt: 2
   }}>
      <content.Icon fontSize='large' sx={{ mb: 2, color: content.iconColor }} />
      {typeof content.text === 'string' && <Typography variant="body2" sx={{ color: themeConfig.primary[600], textAlign: 'center' }}>{content.text}</Typography>}
      {typeof content.text !== 'string' && content.text}

      {props.state === 'failure' && <Button onClick={retryCallback} variant="text" endIcon={<ReplayOutlined />} sx={{ mt: 2 }}>Try again</Button>}
   </Box>
})